import type { Dispatch, SetStateAction } from 'react';

import {
	type ColumnDef,
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	getPaginationRowModel,
	type TableOptions,
	type TableState,
	useReactTable
} from '@tanstack/react-table';
import { FileMinus } from 'lucide-react';

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { numberWithDelimeter } from '@/services/string';

import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Pagination, PaginationContent, PaginationItems } from './pagination';
import { Skeleton } from './skeleton';

interface DataTableProps<TData, TValue> {
	title?: string;
	columns: ColumnDef<TData, TValue>[];
	data: TData[] | undefined;
	pageCount?: number;
	state?: Partial<TableState>;
	setPagination?: Dispatch<
		SetStateAction<{ pageIndex: number, pageSize: number }>
	>;
	setColumnVisibility?: Dispatch<SetStateAction<object>>;
	total?: number | undefined;
	isUsePagination?: boolean;
	isLoading?: boolean;
	tableOptions?: StrictOmit<
		TableOptions<TData>,
		'data' | 'columns' | 'getCoreRowModel'
	>;
	pageSizes?: number[];
}

export function DataTable<TData, TValue>({
	title,
	columns,
	data = Array.from({ length: 10 }),
	pageCount,
	state,
	setPagination,
	setColumnVisibility,
	total = 0,
	isUsePagination = true,
	isLoading = false,
	tableOptions,
	pageSizes = [10, 20, 50, 100]
}: Readonly<DataTableProps<TData, TValue>>) {
	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
		pageCount,
		onPaginationChange: setPagination,
		onColumnVisibilityChange: setColumnVisibility,
		state,
		getPaginationRowModel: getPaginationRowModel(),
		manualPagination: true,
		initialState: {
			columnVisibility: {
				...Object.assign(
					{},
					...columns.map((item) => ({
						[item.meta?.id as string]: item.meta?.isVisible ?? true
					}))
				)
			},
			expanded: state?.expanded
		},
		getExpandedRowModel: getExpandedRowModel(),
		...tableOptions
	});

	if ((!data?.length || data?.filter(Boolean).length === 0) && !isLoading) {
		return (
			<div className="text-secondary mt-2 flex flex-col items-center justify-center gap-3 rounded-sm bg-gray-100 p-8 font-normal">
				<FileMinus />
				<span>Belum Ada data</span>
			</div>
		);
	}
	return (
		<div className="rounded-md">
			<Card className="border-0 px-0">
				{title ? (
					<CardHeader className="border-b-accent-foreground/10 border-b-2 pt-1 pb-3">
						<CardTitle>{title}</CardTitle>
					</CardHeader>
				) : null}
				<CardContent className="p-0">
					<Table>
						<TableHeader>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow
									key={headerGroup.id}
									className="border-b-2 border-solid bg-neutral-100 font-normal text-slate-600"
								>
									{headerGroup.headers.map((header) => {
										return (
											<TableHead
												key={header.id}
												className={cn(
													'p-3 text-left font-bold',
													header.column.columnDef.meta?.headerClassName
												)}
											>
												{header.isPlaceholder
													? null
													: flexRender(header.column.columnDef.header, header.getContext())}
											</TableHead>
										);
									})}
								</TableRow>
							))}
						</TableHeader>
						<TableBody>
							{(!data?.length || data?.filter(Boolean).length === 0) && !isLoading ? (
								<TableRow>
									<TableCell
										className="border border-slate-200 bg-gray-100 p-0"
										colSpan={table.getAllColumns().length}
									>
										<div className="text-secondary flex flex-col items-center justify-center gap-3 p-8 font-normal">
											<FileMinus />
											<span>Belum Ada data</span>
										</div>
									</TableCell>
								</TableRow>
							) : (
								table.getPaginationRowModel().rows.map((row) => (
									<TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
										{row.getVisibleCells().map((cell) => (
											<TableCell
												key={cell.id}
												className={cn(cell.column.columnDef.meta?.className)}
												style={
													cell.column.columnDef.meta?.style
														? cell.column.columnDef.meta.style(
																cell.row.original,
																cell.getValue()
															)
														: {}
												}
											>
												{isLoading ? (
													<Skeleton className="h-4 bg-neutral-200" />
												) : (
													flexRender(cell.column.columnDef.cell, cell.getContext())
												)}
											</TableCell>
										))}
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</CardContent>
			</Card>
			{isUsePagination && total > 0 && !isLoading ? (
				<div className="flex w-full flex-row justify-between px-1 py-0 text-black">
					<div className="flex shrink-0 flex-col items-start gap-2 md:flex-row md:items-center">
						<span className="flex gap-2">
							<p>Tampilan Data</p>
							<select
								className="border-primary-500 rounded-md border px-1"
								value={state?.pagination?.pageSize}
								onChange={(e) => {
									table.setPageSize(Number(e.target.value));
									table.setPageIndex(0);
								}}
							>
								{pageSizes.map((pageSize) => (
									<option key={pageSize} value={pageSize}>
										{pageSize}
									</option>
								))}
								{total < 500 ? <option value={total}>Semua</option> : null}
							</select>
						</span>
						<p> dari {numberWithDelimeter(total)}</p>
					</div>
					<Pagination className="grow items-end justify-end">
						<PaginationContent>
							<PaginationItems
								active={
									state?.pagination?.pageIndex === 0
										? 1
										: (state?.pagination?.pageIndex ?? 0) + 1
								}
								total={total}
								size={state?.pagination?.pageSize ?? 10}
								onClick={(pageIndex) => {
									table.setPageIndex(pageIndex);
								}}
							/>
						</PaginationContent>
					</Pagination>
				</div>
			) : null}
		</div>
	);
}
